import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';

interface BulkKioskSendDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  processing: boolean;
  progress: number;
  results: {
    success: number;
    failed: number;
    total: number;
    alreadyOpen?: number;
    alreadyOpenNames?: string[];
  };
  currentItem: string;
}

export const BulkKioskSendDialog: React.FC<BulkKioskSendDialogProps> = ({
  open,
  onClose,
  onConfirm,
  processing,
  progress,
  results,
  currentItem,
}) => {
  return (
    <Dialog
      open={open}
      onClose={!processing ? onClose : undefined}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Toplu Kiosk'ta Açma
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Seçili {results.total} adet ürün Kiosk'ta açılacaktır.
        </DialogContentText>
        
        {results.alreadyOpen !== undefined && results.alreadyOpen > 0 && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'warning.light', border: '1px solid', borderColor: 'warning.main', borderRadius: 1 }}>
            <Typography variant="body2" color="warning.dark" fontWeight="bold">
              ⚠️ Zaten açık olan ürün sayısı: {results.alreadyOpen}
            </Typography>
            {results.alreadyOpenNames && results.alreadyOpenNames.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Zaten açık ürünler:
                </Typography>
                {results.alreadyOpenNames.slice(0, 5).map((name, idx) => (
                  <Typography key={idx} variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1 }}>
                    • {name}
                  </Typography>
                ))}
                {results.alreadyOpenNames.length > 5 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1 }}>
                    ... ve {results.alreadyOpenNames.length - 5} ürün daha
                  </Typography>
                )}
              </Box>
            )}
            <Typography variant="body2" color="warning.dark" sx={{ mt: 1 }}>
              Bu ürünler tekrar açılmayacak, sadece kapalı olanlar açılacaktır.
            </Typography>
          </Box>
        )}
        
        {processing && (
          <Box sx={{ width: '100%', mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              İşleniyor: {currentItem}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: '100%', mr: 1 }}>
                <LinearProgress variant="determinate" value={progress} color="info" />
              </Box>
              <Box sx={{ minWidth: 35 }}>
                <Typography variant="body2" color="text.secondary">
                  {`${progress}%`}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', border: '1px solid #ddd', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                İşlem Durumu:
              </Typography>
              <Typography variant="body2" color="success.main">
                ✅ Başarılı: {results.success}
              </Typography>
              <Typography variant="body2" color="error.main">
                ❌ Başarısız: {results.failed}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                📊 Toplam: {results.total}
              </Typography>
            </Box>
          </Box>
        )}
        
        {!processing && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', border: '1px solid #ddd', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              İşlem Detayları:
            </Typography>
            <Typography variant="body2">
              • Toplam açılacak/kapatılacak ürün sayısı: {results.total}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Her ürün tek tek Kiosk'ta açılacak/kapatılacaktır.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onClose} 
          color="primary"
          disabled={processing}
        >
          {processing ? 'İşlem Devam Ediyor...' : 'İptal'}
        </Button>
        {!processing && (
          <Button 
            onClick={onConfirm} 
            color="info" 
            variant="contained"
            startIcon={<StorefrontIcon />}
          >
            Kiosk'ta Aç
          </Button>
        )}
        {processing && progress === 100 && (
          <Button 
            onClick={onClose} 
            color="primary" 
            variant="contained"
          >
            Tamam
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

